/*
  Discount tier progress card on the product page. Markup, styles and the
  first paint: snippets/tier-progress.liquid. This file keeps it live.

  The card counts the cart total before discounts (original_total_price),
  the total Shopify checks tier minimums against; the snippet says why. What
  is picked on the page does not count until it is in the cart, so the card
  redraws whenever the cart changes, whatever changed it: the theme, the
  UpCart slide-out cart, an upsell app, another tab.
*/
(function () {
  'use strict';

  if (window.TierProgress) return;

  var instances = [];

  // Where a cart total sits against the tiers (sorted by min, cents)
  function tierState(tiers, total) {
    var prev = 0;
    var next = null;
    var done = null;
    var steps = tiers.map(function (tier) {
      var span = Math.max(tier.min - prev, 1);
      var fill = Math.min(100, Math.max(0, Math.floor((total - prev) * 100 / span)));
      var reached = total >= tier.min;
      var isNext = false;
      if (reached) {
        if (tier.save > 0) done = tier;
      } else if (!next) {
        next = tier;
        isNext = true;
      }
      prev = tier.min;
      return { fill: fill, reached: reached, isNext: isNext };
    });
    return { steps: steps, next: next, gap: next ? next.min - total : 0, done: done };
  }

  // Matches the money_without_trailing_zeros filter: "$80.01", "$80"
  function money(cents) {
    var text;
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      text = theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
        .replace('<sup>', '.')
        .replace('</sup>', '');
    } else {
      text = '$' + (cents / 100).toFixed(2);
    }
    return text.replace(/[.,]00(?!\d)/, '');
  }

  function strong(text) {
    return '<strong>' + text + '</strong>';
  }

  function messageHtml(state, copy) {
    if (state.next && state.next.save > 0) {
      return copy.saveNext
        .replace('[amount]', strong(money(state.gap)))
        .replace('[reward]', strong(state.next.reward));
    }
    if (state.next) {
      return copy.shipNext.replace('[amount]', strong(money(state.gap)));
    }
    if (state.done) {
      return copy.saveDone.replace('[reward]', strong(state.done.reward));
    }
    return copy.shipDone;
  }

  function TierProgress(el) {
    var config = JSON.parse(el.querySelector('[data-tier-config]').textContent);

    this.el = el;
    this.tiers = config.tiers;
    this.copy = config.copy;
    this.cart = config.cart;
    this.message = el.querySelector('[data-tier-message]');
    this.fills = el.querySelectorAll('[data-tier-fill]');
    this.stepEls = el.querySelectorAll('[data-tier-step]');
  }

  TierProgress.prototype.render = function () {
    var state = tierState(this.tiers, this.cart);
    var html = messageHtml(state, this.copy);

    // Only touch the live region when the words change, so a screen reader
    // is not told the same thing twice
    if (this.message.innerHTML !== html) this.message.innerHTML = html;

    for (var i = 0; i < state.steps.length; i++) {
      var step = state.steps[i];
      if (this.fills[i]) this.fills[i].style.width = step.fill + '%';
      if (this.stepEls[i]) {
        this.stepEls[i].classList.toggle('is-reached', step.reached);
        this.stepEls[i].classList.toggle('is-next', step.isNext);
      }
    }
  };

  TierProgress.prototype.setCart = function (cart) {
    if (!cart || typeof cart.original_total_price !== 'number') return;
    this.cart = cart.original_total_price;
    this.render();
  };

  function init(root) {
    (root || document).querySelectorAll('[data-tier-progress]').forEach(function (el) {
      if (el.tierProgress) return;
      el.tierProgress = new TierProgress(el);
      instances.push(el.tierProgress);
    });
  }

  function updateAll(cart) {
    instances = instances.filter(function (instance) { return document.contains(instance.el); });
    instances.forEach(function (instance) { instance.setCart(cart); });
  }

  var latestFetch = 0;
  var refreshTimer = null;

  function refreshCart() {
    if (!instances.length) return;
    var url = (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
    // Only the newest read may draw, so a slow older reply cannot undo it
    var thisFetch = ++latestFetch;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) { if (thisFetch === latestFetch) updateAll(cart); })
      .catch(function () {});
  }

  // One read for a burst of writes, e.g. a drawer that changes a line and
  // then adds a free gift
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCart, 100);
  }

  /*
    Cart writes from any script on the page. UpCart and other cart apps change
    the cart with their own requests and fire none of the theme's events, and
    UpCart already wraps window.fetch and XMLHttpRequest. Wrapping them again
    would race with it, so this watches finished requests through Resource
    Timing instead, which touches nothing. It matches /cart/add, /cart/change,
    /cart/update and /cart/clear (with .js, a query or a locale prefix). The
    card's own read is /cart.js, which does not match, so it cannot loop.
  */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js(on)?)?$/;

  function watchCartWrites() {
    if (!('PerformanceObserver' in window)) return;
    try {
      new PerformanceObserver(function (list) {
        var wrote = list.getEntries().some(function (entry) {
          return (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') &&
            CART_WRITE.test(entry.name.split(/[?#]/)[0]);
        });
        if (wrote) scheduleRefresh();
      }).observe({ entryTypes: ['resource'] });
    } catch (e) {}
  }

  watchCartWrites();

  // Theme events stay as a fallback for browsers without PerformanceObserver.
  // The theme also fires ajaxProduct:added after a failed add, which only
  // costs a read that finds the cart unchanged.
  document.addEventListener('ajaxProduct:added', scheduleRefresh);

  // The theme's cart drawer sends the new cart with this, so no read is needed
  document.addEventListener('cart:updated', function (evt) {
    updateAll(evt.detail && evt.detail.cart);
  });

  // Back/forward cache can restore the page with an old cart
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) refreshCart();
  });

  // The cart may have changed in another tab while this one was hidden
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refreshCart();
  });

  document.addEventListener('quickview:loaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (evt) { init(evt.target); });

  window.TierProgress = { init: init, tierState: tierState };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
